import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { UserStateService } from '../shared/state/user.state';

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  private userState = inject(UserStateService);
  private router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): boolean | UrlTree {
    // Check if user is authenticated
    const isAuth = this.userState.isAuthenticated();
    const sessionStatus = this.userState.sessionStatus();
    const redirectFromPaths = [
      '',
      'login',
      'signup',
      'activate',
      'recoverpassword',
      'resetpassword',
    ];
    const redirect = redirectFromPaths.includes(route.routeConfig?.path || '');
    const isOnboardingRoute = route.routeConfig?.path === 'onboarding';

    // Check if there's a stored redirect URL from registration
    const storedRedirectUrl = sessionStorage.getItem('authRedirectUrl');

    // Get the full URL path
    const fullPath = this.getFullPath(route);

    if (isAuth) {
      // Onboarding-required users can only access /onboarding
      if (sessionStatus === 'onboarding_required') {
        return isOnboardingRoute ? true : this.router.createUrlTree(['/onboarding']);
      }

      // Ready users should not access /onboarding
      if (isOnboardingRoute && sessionStatus === 'ready') {
        return this.router.createUrlTree(['/applications']);
      }

      if (redirect) {
        // If there's a stored redirect URL, use it
        if (storedRedirectUrl) {
          sessionStorage.removeItem('authRedirectUrl'); // Clear it after use
          return this.router.createUrlTree([storedRedirectUrl]);
        }

        // Don't redirect if we're already going to a specific path
        if (fullPath.startsWith('/profile/')) {
          return true;
        }

        return this.router.createUrlTree(['/applications']);
      }
      return true;
    }
    if (redirect) {
      return true;
    }
    // Not authenticated: redirect to OIDC login
    this.userState.loginWithRedirect();
    return false;
  }

  /**
   * Get the full path from the route snapshot
   */
  private getFullPath(route: ActivatedRouteSnapshot): string {
    if (!route.parent) {
      return route.url.map(segment => segment.path).join('/');
    }

    return this.getFullPath(route.parent) + '/' + route.url.map(segment => segment.path).join('/');
  }
}
